import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import { getPublicTracking } from '../services/tracking';
import { SUPPORTED_LANGUAGES } from '../i18n/i18n';

/**
 * Public customer tracking page — reached from the link the office sends
 * (app/t/<token>). No login. Shows the job's progress in six customer-facing
 * steps, the planned date, the notes the company chose to share (with
 * photos), the pickup receipt / invoice downloads and — at Completed — the
 * Google review button. Refreshes on open and every 60 s.
 *
 * An expired or revoked link (API 410) shows only the company's contact
 * details; an unknown link (404) shows "not valid".
 */

const STAGES = ['RECEIVED', 'CONFIRMED', 'SCHEDULED', 'IN_PROGRESS', 'READY', 'COMPLETED'];

const styles = `
  .tr{--blue:#2563EB;--ink:#0F172A;--muted:#64748B;--line:#E2E8F0;--bg:#F8FAFC;--ok:#15803D;font-family:'Inter',-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;background:var(--bg);min-height:100vh;color:var(--ink);-webkit-font-smoothing:antialiased}
  .tr *{box-sizing:border-box}
  .tr .wrap{max-width:560px;margin:0 auto;padding:22px 16px 48px}
  .tr .head{display:flex;align-items:center;gap:12px;margin-bottom:16px}
  .tr .head img{width:48px;height:48px;border-radius:12px;object-fit:cover;border:1px solid var(--line);background:#fff}
  .tr .head .co{font-size:12px;font-weight:700;color:var(--muted);letter-spacing:.04em;text-transform:uppercase}
  .tr .head h1{font-size:20px;font-weight:800;margin:2px 0 0;letter-spacing:-.3px}
  .tr .lang{margin-left:auto;display:flex;gap:4px}
  .tr .lang button{border:1px solid var(--line);background:#fff;border-radius:999px;padding:4px 10px;font:700 12px inherit;color:var(--muted);cursor:pointer}
  .tr .lang button.on{border-color:var(--blue);color:var(--blue)}
  .tr .card{background:#fff;border:1px solid var(--line);border-radius:16px;padding:18px;box-shadow:0 1px 2px rgba(15,23,42,.04);margin-bottom:14px}
  .tr .card h2{font-size:11px;font-weight:800;letter-spacing:.08em;text-transform:uppercase;color:var(--muted);margin:0 0 12px}
  .tr .stage-now{display:flex;align-items:baseline;gap:10px;margin-bottom:16px}
  .tr .stage-now b{font-size:22px;font-weight:800}
  .tr .stage-now span{font-size:12.5px;color:var(--muted)}
  .tr .steps{display:grid;grid-template-columns:repeat(6,1fr);gap:4px}
  .tr .step{display:flex;flex-direction:column;align-items:center;gap:6px;text-align:center;font-size:10.5px;font-weight:700;color:var(--muted)}
  .tr .step i{width:100%;height:6px;border-radius:999px;background:var(--line);display:block}
  .tr .step.done{color:var(--ok)}.tr .step.done i{background:var(--ok)}
  .tr .step.now{color:var(--blue)}.tr .step.now i{background:var(--blue)}
  .tr .cancel{padding:12px 14px;border-radius:12px;background:#FEF2F2;color:#B91C1C;font-weight:600;font-size:14px}
  .tr .row{display:flex;justify-content:space-between;gap:12px;padding:9px 0;border-top:1px solid var(--line);font-size:14px}
  .tr .row:first-of-type{border-top:0}
  .tr .row span{color:var(--muted)}.tr .row b{text-align:right}
  .tr .note{padding:12px 0;border-top:1px solid var(--line)}
  .tr .note:first-of-type{border-top:0;padding-top:0}
  .tr .note time{display:block;font-size:11.5px;color:var(--muted);margin-bottom:4px}
  .tr .note p{margin:0;font-size:14.5px;line-height:1.5;white-space:pre-wrap}
  .tr .thumbs{display:flex;flex-wrap:wrap;gap:8px;margin-top:10px}
  .tr .thumbs button{border:1px solid var(--line);border-radius:10px;padding:0;overflow:hidden;background:#fff;width:84px;height:84px;cursor:pointer}
  .tr .thumbs img{width:100%;height:100%;object-fit:cover;display:block}
  .tr .btn{display:flex;align-items:center;justify-content:center;width:100%;border:0;border-radius:12px;padding:13px;background:var(--blue);color:#fff;font:800 14px inherit;text-decoration:none;cursor:pointer;margin-top:10px}
  .tr .btn.ghost{background:#fff;color:var(--ink);border:1px solid var(--line)}
  .tr .btn.review{background:var(--ok)}
  .tr .contact{font-size:14px}.tr .contact a{color:var(--blue);text-decoration:none;font-weight:600}
  .tr .empty{color:var(--muted);font-size:14px}
  .tr .foot{font-size:11.5px;color:var(--muted);text-align:center;margin-top:8px}
  .tr .lb{position:fixed;inset:0;background:rgba(15,23,42,.9);display:flex;align-items:center;justify-content:center;z-index:50;padding:16px}
  .tr .lb img{max-width:100%;max-height:100%;border-radius:10px}
  .tr .lb button{position:absolute;top:14px;right:14px;border:0;background:#fff;border-radius:999px;width:36px;height:36px;font:800 18px inherit;cursor:pointer}
`;

// Intl locale tag for the page language (Serbian is Latin script, sr-Latn-RS);
// anything unknown falls back to English (en-GB).
const intlTag = (lang) => (SUPPORTED_LANGUAGES.find(l => l.code === lang) || SUPPORTED_LANGUAGES[0]).intl;

const fmtDate = (value, lang, withTime = true) => {
    if (!value) return null;
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleString(intlTag(lang), withTime
        ? { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }
        : { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const fmtTime = (value, lang) => {
    if (!value) return null;
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleString(intlTag(lang), { hour: '2-digit', minute: '2-digit' });
};

const fmtDateRange = (from, to, lang) => {
    const base = fmtDate(from, lang);
    if (!base) return null;
    const endTime = fmtTime(to, lang);
    if (!endTime) return base;
    return `${base} – ${endTime}`;
};

const Contact = ({ company, t }) => (
    <div className="card contact">
        <h2>{t('track.contact')}</h2>
        <div><b>{company?.name}</b></div>
        {company?.phone && <div>{t('track.phone')}: <a href={`tel:${company.phone}`}>{company.phone}</a></div>}
        {company?.email && <div>{t('track.email')}: <a href={`mailto:${company.email}`}>{company.email}</a></div>}
    </div>
);

const TrackPage = () => {
    const { token } = useParams();
    const { t, i18n } = useTranslation();
    const [lightbox, setLightbox] = useState(null);

    const { data, isLoading, dataUpdatedAt } = useQuery(['public-track', token], () => getPublicTracking(token), {
        enabled: !!token,
        refetchInterval: 60 * 1000,
        refetchOnWindowFocus: true,
        retry: false,
    });

    const code = data?.code;
    const page = code === 200 ? data.message : null;
    const expired = code === 410 ? data.message : null;
    const company = page?.company || expired?.company;

    useEffect(() => { document.title = company ? `${company.name} — ${t('track.title')}` : 'Soroby'; }, [company, t]);
    useEffect(() => {
        if (!lightbox) return undefined;
        const onKey = (e) => e.key === 'Escape' && setLightbox(null);
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [lightbox]);

    const stage = page?.job?.stage;
    const stageIdx = STAGES.indexOf(stage);
    const cancelled = stage === 'CANCELLED';
    const plannedLabel = page?.job?.pickupType === 'CUSTOMER_DELIVERS' ? t('track.dropoff') : t('track.pickup');

    return (
        <div className="tr">
            <style>{styles}</style>
            <div className="wrap">
                <div className="head">
                    {company?.logoUrl && <img src={company.logoUrl} alt="" />}
                    <div>
                        <div className="co">{company?.name || 'Soroby'}</div>
                        <h1>{t('track.title')}</h1>
                    </div>
                    <div className="lang">
                        {SUPPORTED_LANGUAGES.map(({ code }) => (
                            <button type="button" key={code} className={i18n.language === code ? 'on' : ''} onClick={() => i18n.changeLanguage(code)}>{code.toUpperCase()}</button>
                        ))}
                    </div>
                </div>

                {isLoading && !data && <div className="card empty">{t('track.loading')}</div>}

                {code === 404 && <div className="card"><b>{t('track.invalidTitle')}</b><p className="empty">{t('track.invalid')}</p></div>}
                {code === 429 && <div className="card empty">{t('track.tooMany')}</div>}

                {expired && (
                    <>
                        <div className="card"><b>{t('track.expiredTitle')}</b><p className="empty" style={{ margin: '6px 0 0' }}>{t('track.expired')}</p></div>
                        <Contact company={expired.company} t={t} />
                    </>
                )}

                {page && (
                    <>
                        <div className="card">
                            <h2>{t('track.order')} #{page.job.number}</h2>
                            <div className="stage-now"><b>{t(`track.stages.${stage}`) || page.job.stageLabel}</b><span>{t('track.status')}</span></div>
                            {cancelled
                                ? <div className="cancel">{t('track.cancelled')}</div>
                                : <div className="steps">
                                    {STAGES.map((s, i) => (
                                        <div key={s} className={`step ${i < stageIdx ? 'done' : ''} ${i === stageIdx ? 'now' : ''}`}>
                                            <i /> <span>{t(`track.stages.${s}`)}</span>
                                        </div>
                                    ))}
                                </div>
                            }
                        </div>

                        {!cancelled && (
                            <div className="card">
                                <h2>{t('track.planned')}</h2>
                                <div className="row"><span>{plannedLabel}</span><b>{fmtDateRange(page.job.plannedAt, page.job.plannedAtTo, i18n.language) || t('track.notPlanned')}</b></div>
                                {page.job.deliveryAt && <div className="row"><span>{t('track.delivery')}</span><b>{fmtDateRange(page.job.deliveryAt, page.job.deliveryAtTo, i18n.language)}</b></div>}
                                {page.job.completedAt && <div className="row"><span>{t('track.completed')}</span><b>{fmtDate(page.job.completedAt, i18n.language)}</b></div>}
                            </div>
                        )}

                        <div className="card">
                            <h2>{t('track.updates')}</h2>
                            {!page.notes?.length && <div className="empty">{t('track.noUpdates')}</div>}
                            {page.notes?.map((note, i) => (
                                <div className="note" key={`n-${i}`}>
                                    <time>{fmtDate(note.createdAt, i18n.language)}</time>
                                    {note.body && <p>{note.body}</p>}
                                    {note.photos?.length > 0 && (
                                        <div className="thumbs">
                                            {note.photos.map((url, pi) => (
                                                <button type="button" key={`p-${pi}`} onClick={() => setLightbox(url)} title={t('track.photo')}>
                                                    <img src={url} alt={`${t('track.photo')} ${pi + 1}`} loading="lazy" />
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>

                        {(page.receipt?.url || page.invoice?.url) && (
                            <div className="card">
                                <h2>{t('track.documents')}</h2>
                                {page.receipt?.url && <a className="btn ghost" href={page.receipt.url} target="_blank" rel="noopener noreferrer">{t('track.receipt')}</a>}
                                {page.invoice?.url && <a className="btn" href={page.invoice.url} target="_blank" rel="noopener noreferrer">{t('track.invoice')}</a>}
                            </div>
                        )}

                        {page.review?.url && (
                            <div className="card">
                                <h2>{t('track.reviewTitle')}</h2>
                                <div className="empty">{t('track.review')}</div>
                                <a className="btn review" href={page.review.url} target="_blank" rel="noopener noreferrer">{t('track.reviewBtn')}</a>
                            </div>
                        )}

                        <Contact company={page.company} t={t} />
                        {dataUpdatedAt > 0 && <div className="foot">{t('track.updated')} {fmtDate(dataUpdatedAt, i18n.language)}</div>}
                    </>
                )}
            </div>

            {lightbox && (
                <div className="lb" onClick={() => setLightbox(null)} role="dialog" aria-label={t('track.photo')}>
                    <button type="button" aria-label={t('track.close')} onClick={() => setLightbox(null)}>×</button>
                    <img src={lightbox} alt={t('track.photo')} onClick={(e) => e.stopPropagation()} />
                </div>
            )}
        </div>
    );
};

export default TrackPage;
